import { A } from '@ember/array';
import type ArrayProxy from '@ember/array/proxy';
import { on } from '@ember/modifier';
import { get } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task, timeout, all } from 'ember-concurrency';
import pluralize from 'ember-inflector/lib/helpers/pluralize';
import set from 'ember-set-helper/helpers/set';
import { and, not, or } from 'ember-truth-helpers';
import PropagableSettingsChangedModal from 'garaje/components/propagable-settings-changed/modal';
import config from 'garaje/config/environment';
import type GlobalSettingBatch from 'garaje/models/global-setting-batch';
import type { AllPropagableModel } from 'garaje/models/global-setting-batch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type GlobalSettingBatchService from 'garaje/services/global-setting-batch';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import zft from 'garaje/utils/zero-for-tests';
import { localCopy } from 'tracked-toolbox';

interface PropagableSettingsChangedSignature {
  Args: {
    forType?: 'locations' | 'flows';
  };
}

export default class PropagableSettingsChanged extends Component<PropagableSettingsChangedSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: StoreService;
  @service('global-setting-batch') declare globalSettingBatchService: GlobalSettingBatchService;

  @localCopy('args.forType', 'locations') forType!: string;

  @tracked wantsToUpdateSettings = false;
  @tracked showUndo = true;
  @tracked selectedChildren: AllPropagableModel[] = [];

  get globalSettingBatch(): GlobalSettingBatch {
    return this.globalSettingBatchService.currentGlobalSettingBatch;
  }

  get typeSingular(): string {
    return this.forType.slice(0, -1);
  }

  get totalChildren(): number {
    return this.globalSettingBatch.hasMany('children').ids().length;
  }

  stateNameChanged = (): void => {
    const globalSettingBatch = this.globalSettingBatch;
    // eslint-disable-next-line ember/no-get
    const stateName = get(globalSettingBatch, 'currentState.stateName');
    const globalSettingBatchService = this.globalSettingBatchService;
    if (stateName === 'root.loaded.saved' && config.environment !== 'test') {
      // Hide undo message after 10 seconds
      // eslint-disable-next-line ember/no-runloop
      later(
        this,
        () => {
          if (!this.isDestroyed) {
            this.showUndo = false;
          }
          globalSettingBatchService.switchWithExitingCopy();
        },
        10000,
      );
    } else {
      this.showUndo = true;
    }
  };

  undoTask = task(async () => {
    const globalSettingBatch = this.globalSettingBatch;
    const globalSettingBatchService = this.globalSettingBatchService;
    try {
      await timeout(zft(500));
      await globalSettingBatch.undo();
      globalSettingBatchService.switchWithExitingCopy();
      this.flashMessages.showAndHideFlash('success', 'Undone!');
    } catch (_e) {
      this.showUndo = false;
      this.flashMessages.showAndHideFlash('error', 'There was an error.');
    }
  });

  overwriteTask = task(async () => {
    const { globalSettingBatch, selectedChildren } = this;

    const children = await globalSettingBatch.children;
    children.pushObjects(selectedChildren as unknown as ArrayProxy<AllPropagableModel>);

    try {
      await this.globalSettingBatchService.makeACopy();
      await globalSettingBatch.save({ adapterOptions: { restoreAttributes: true } });
      await all(selectedChildren.map((child) => child.propagableReload()));
      this.flashMessages.showAndHideFlash('success', 'Overwritten!');
    } catch (e) {
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
      globalSettingBatch.unloadRecord();
    }

    this.close();
  });

  close = (): void => {
    this.wantsToUpdateSettings = false;
    this.selectedChildren = A();
  };

  toggleWantsToUpdate = (): void => {
    this.wantsToUpdateSettings = !this.wantsToUpdateSettings;
  };

  <template>
    {{#if (and this.globalSettingBatch.totalSettingsChanged (not this.globalSettingBatch.isDeleted))}}
      {{! @glint-expect-error type ember data type issue }}
      {{#if this.globalSettingBatch.isNew}}
        <span
          data-test-propagable-changes
          class="propagable-settings float-right"
          {{on "click" this.toggleWantsToUpdate}}
          {{didUpdate this.stateNameChanged this.globalSettingBatch.currentState.stateName}}
        >
          Update
          <span data-test-total-changes>{{pluralize this.globalSettingBatch.totalSettingsChanged "setting"}}</span>
          in more locations
        </span>

        {{#if this.wantsToUpdateSettings}}
          <PropagableSettingsChangedModal
            @forType={{this.forType}}
            @globalSettingBatch={{this.globalSettingBatch}}
            @overwriteTask={{this.overwriteTask}}
            @selectedChildren={{this.selectedChildren}}
            @updateSelectedChildren={{set this "selectedChildren"}}
            @close={{this.close}}
          />
        {{/if}}
      {{else if this.showUndo}}
        <span
          data-test-propagable-undo
          class="propagable-settings undo float-right"
          {{didUpdate this.stateNameChanged this.globalSettingBatch.currentState.stateName}}
        >
          <span data-test-total-changes>{{pluralize this.globalSettingBatch.totalSettingsChanged "setting"}}</span>
          updated at
          <span data-test-total-child>{{pluralize this.totalChildren this.typeSingular}}</span>.
          {{#if (or this.undoTask.isRunning this.globalSettingBatch.isDeleted)}}
            <strong data-test-undo-task class="disabled">Undoing...</strong>
          {{else}}
            <strong data-test-undo-task {{on "click" this.undoTask.perform}}>Undo</strong>
          {{/if}}
        </span>
      {{/if}}
    {{/if}}
  </template>
}
